/**
 * Ankeraufloesung und Faelligkeit - Anzeigefassung (Spec 2026-09-07, Abschnitt 2).
 *
 * Zwillingsdatei von backend/src/domaene/anker.ts. Beide werden von derselben Faelletabelle geprueft
 * (shared/anker-faelle.json). Der Unterschied ist allein die Eingangsform: hier kommt eine `Order`
 * mit camelCase-Feldern und Zeichenketten-Zahlen aus dem Bestand, dort eine Zeile aus der Datenbank.
 */
import type { Order, OrderPayment } from "./types.ts";
import { paymentStatusOf } from "./payments.ts";

export type Anker = "order_date" | "etd" | "eta" | "goods_received";
export type Basis = "ist" | "prognose" | "hand" | "eingefroren" | "fehlt";

export interface Ankerdaten {
  orderDate?: string | null;
  etd?: string | null;
  eta?: string | null;
  etdPi?: string | null;
  airEtd?: string | null;
  airEta?: string | null;
  roadEtd?: string | null;
  roadEta?: string | null;
  ekaReceivedDate?: string | null;
  transportMode?: string | null;
  /** R8: mit DSV verknuepft (`Order.dsvVerknuepft`) - entscheidet, woher die ETD kommt. */
  dsvVerknuepft?: boolean | null;
  /** `Order.stockOrder`: bei Stock Order OHNE DSV-Sendung haben ETD/ETA-Raten kein Datum (Abruf offen). */
  stockOrder?: boolean | null;
}

export interface Ankerergebnis {
  datum: string | null;
  basis: Basis;
}

/**
 * Zentraler Katalog der Anzeigenamen der vier Anker (Nutzerentscheidung 01.10.2026) - der Titel einer
 * Zahlungsvorlage ("80 % 20 Tage nach ETD", `lib/zahlungsvorlagenTitel.ts`) liest ihn, ebenso der
 * Zwilling im Backend (`domaene/zahlungsvorlagenTitel.ts`; beide gegen
 * `shared/zahlungsvorlagen-titel-faelle.json`). `null` heisst "von Hand gesetzt" und heisst vorerst
 * "Handdatum" (ein weiterer Anker "Fix-Datum" ist offen und noch nicht gebaut).
 */
export const ANKER_ANZEIGENAME: Readonly<Record<Anker, string>> = Object.freeze({
  order_date: "Bestelldatum",
  etd: "ETD",
  eta: "ETA",
  goods_received: "Wareneingang",
});
export const HANDDATUM_ANZEIGENAME = "Handdatum";

/**
 * Beschriftung der vier Anker im Auswahlmenue - eine Wertquelle, von Ratenplan und Katalogdialog
 * benutzt. Gleich dem Katalog oben, nur dass das Menue beim ETD den Zusatz "(BL)" traegt, weil dort
 * erklaert werden muss, was der logische Anker bedeutet.
 */
export const ANKER_LABEL: Readonly<Record<Anker, string>> = Object.freeze({
  ...ANKER_ANZEIGENAME,
  etd: "ETD (BL)",
});

function wert(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const roh = String(v).slice(0, 10).trim();
  return roh === "" ? null : roh;
}

/**
 * Stock Order (docs/superpowers/specs/2026-10-04-stock-order-abruf.md): eine Stock Order liegt bei Minwa
 * auf Lager, bis wir sie abrufen; abgerufen ist sie, sobald ihr eine DSV-Sendung zugeordnet ist. Davor
 * sagt weder die PI-ETD (nur Produktionsende) noch ein Handwert von ETD/ETA etwas ueber die Zahlung
 * aus: Raten mit Anker etd/eta haben kein Datum. Handdatum, Status fixed/paid, Bestelldatum und
 * Wareneingang bleiben unberuehrt.
 */
export function wartetAufAbruf(daten: Ankerdaten, anker: Anker | null): boolean {
  return (anker === "etd" || anker === "eta") && daten.stockOrder === true && daten.dsvVerknuepft !== true;
}

/**
 * R8 (Nutzerentscheidung 01.10.2026): der Anker "etd" haengt an der DSV-Verknuepfung. Verknuepft ->
 * die DSV-gespeisten Felder (unten), Basis "ist". Nicht verknuepft -> ausschliesslich etd_pi (Basis
 * "prognose"), ein von Hand gesetztes etd/air_etd/road_etd wird IGNORIERT. Beides leer -> kein Datum.
 *
 * Der logische Anker "etd" steht fuer das BL-Datum, das es im Datenmodell nicht gibt. Bei Luftfracht
 * loest er auf air_etd auf, bei Strassenfracht auf road_etd - NICHT auf das Seefracht-etd: ein dort
 * stehengebliebener Wert stammt aus einer frueheren Planung oder einem anderen Modus und waere eine
 * stille Falschaussage. Der Rueckfall ist in allen drei Faellen etd_pi - die ETD aus der
 * Proforma-Rechnung - und faerbt das Ergebnis als Prognose. Dasselbe gilt spiegelbildlich fuer
 * "eta": road_eta ist die geplante Ankunft, KEIN Rueckfall auf das See-eta.
 *
 * `anker` kommt aus einer text-Spalte ohne CHECK (Ratenzeilen aus payment_templates.rows sind
 * ungeprueftes JSONB) - ein unbekannter Wert darf deshalb NICHT im letzten Zweig landen, sondern
 * muss explizit als "goods_received" erkannt und sonst als "fehlt" beantwortet werden.
 */
export function ankerDatum(daten: Ankerdaten, anker: Anker | null): Ankerergebnis {
  if (anker === null) return { datum: null, basis: "fehlt" };
  const luft = daten.transportMode === "air";
  const strasse = daten.transportMode === "road";

  if (wartetAufAbruf(daten, anker)) return { datum: null, basis: "fehlt" };
  if (anker === "order_date") {
    const d = wert(daten.orderDate);
    return d ? { datum: d, basis: "ist" } : { datum: null, basis: "fehlt" };
  }
  if (anker === "etd") {
    // R8: nur eine MIT DSV verknuepfte Bestellung hat eine gefuehrte ETD; sonst zaehlt allein die
    // PI-ETD, ein von Hand gesetztes etd/air_etd/road_etd wird ignoriert.
    if (daten.dsvVerknuepft === true) {
      const d = wert(luft ? daten.airEtd : strasse ? daten.roadEtd : daten.etd);
      if (d) return { datum: d, basis: "ist" };
    }
    const prognose = wert(daten.etdPi);
    return prognose ? { datum: prognose, basis: "prognose" } : { datum: null, basis: "fehlt" };
  }
  if (anker === "eta") {
    const d = wert(luft ? daten.airEta : strasse ? daten.roadEta : daten.eta);
    return d ? { datum: d, basis: "ist" } : { datum: null, basis: "fehlt" };
  }
  if (anker === "goods_received") {
    const d = wert(daten.ekaReceivedDate);
    return d ? { datum: d, basis: "ist" } : { datum: null, basis: "fehlt" };
  }
  return { datum: null, basis: "fehlt" };
}

export function tageAddieren(datum: string, tage: number): string {
  const [jahr, monat, tag] = datum.slice(0, 10).split("-").map(Number);
  const ms = Date.UTC(jahr, monat - 1, tag) + tage * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

/**
 * Die drei Faelle der Spec, in dieser Reihenfolge - die Reihenfolge ist Teil der Aussage:
 *   anchor null    -> Handdatum, wird nie gerechnet
 *   status != open -> eingefroren (fixiert oder bezahlt)
 *   sonst          -> Anker + Offset
 *
 * `dueDays` ist im Bestand eine ZEICHENKETTE (config/payments.ts) und darf leer sein - leer heisst
 * null Tage Versatz, nicht "kein Anker". Das Feld `due` traegt die Faelligkeit, nicht `dueDate`:
 * siehe die bekannte Doppelform bei vom Server geladenen Zeilen (lib/payments.ts#normalizePaymentRow).
 * Der Status kommt aus `paymentStatusOf` - der einzigen Bestandsstelle fuer die Statusbestimmung
 * (lib/payments.ts) - statt aus einem eigenen `String(rate.status ?? "open")`: eine Zeile mit
 * `status: undefined, paid: true` rechnet sonst neu, waehrend der Bestand sie als "bezahlt" anzeigt.
 */
export function faelligkeitVonRate(rate: OrderPayment, o: Order): Ankerergebnis {
  const anker = (rate.anchor as Anker | null | undefined) ?? null;
  if (anker === null) return { datum: wert(rate.due), basis: "hand" };
  const status = paymentStatusOf(rate);
  const gespeichert = wert(rate.due);
  // Eine fixierte/bezahlte Rate MIT Datum bleibt eingefroren. OHNE Datum zeigt sie den Wert, den der
  // serverseitige Nachzug schreiben wuerde (R1/R6): sonst stuende dort "eingefroren" ohne Datum.
  if (status !== "open" && gespeichert !== null) return { datum: gespeichert, basis: "eingefroren" };

  const treffer = ankerDatum(o as unknown as Ankerdaten, anker);
  if (treffer.datum === null) return treffer;
  const tage = Number(rate.dueDays ?? 0);
  return {
    datum: tageAddieren(treffer.datum, Number.isFinite(tage) ? tage : 0),
    basis: treffer.basis,
  };
}

/**
 * Benennt das Feld, dessen Fehlen eine Ankerrate ohne Datum laesst (R6) - mit der Frachtart, weil
 * "ETD" je nach transportMode ein anderes Feld ist (siehe `ankerDatum`). `null` bei Handdatum.
 */
export function fehlendesAnkerfeld(daten: Ankerdaten, anker: Anker | null): string | null {
  if (anker === null) return null;
  if (anker === "order_date") return ANKER_ANZEIGENAME.order_date;
  if (anker === "goods_received") return ANKER_ANZEIGENAME.goods_received;
  // R8: ohne DSV-Verknuepfung fehlt allein die PI-ETD (ein Hand-ETD zaehlt nicht).
  if (anker === "etd" && daten.dsvVerknuepft !== true) return "ETD (PI)";
  const art =
    daten.transportMode === "air" ? "Luftfracht" : daten.transportMode === "road" ? "Straße" : "Seefracht";
  return `${ANKER_ANZEIGENAME[anker]} (${art})`;
}
